import { FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { checkFormFindTicket } from 'pages/avia/main-page/lib/checkFormFindTicket'

import LevelClassNCount, { CountAndClass } from 'widgets/level-class-count'

import { normalizeRegularSearchDirections } from 'entities/ticket/lib/normalize-regular-search-directions'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import { IFindTicketForm } from 'entities/ticket/models/find-ticket/models'

import { TicketClassList } from 'shared/constants'
import { dateFormat } from 'shared/lib/dateFormat'
import { IErrorForm, getFieldsError } from 'shared/lib/validate'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonIconText } from 'shared/ui/button-icon-text'
import { Button } from 'shared/ui/button/button'
import { DepArrCountryBlock } from 'shared/ui/dep-arr-country-block'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import s from './styles.module.scss'

interface Props {
  isSearch?: boolean
  type?: 'white' | 'grey' | 'lightGrey'
  searchData: IFindTicketForm | Partial<IFindTicketForm>
  onChange?: (data?: CountAndClass) => void
  onSwitch?: () => void
  onSearch?: () => void
}

export const SearchFormCopy: FC<Props> = observer(
  ({ isSearch, type, searchData, onChange, onSwitch, onSearch }) => {
    const navigate = useNavigate()

    const { closeDialog, openDialog } = useDialogBottomSheet()

    const { t } = useTranslation()

    const [errors, setErrors] = useState<IErrorForm | undefined>(undefined)

    const directionData = searchData?.directions?.[0]

    const onClickToSearch = (route: string) => {
      navigate({
        pathname: RoutingPaths.avia.searchDirection.path(route),
        search: isSearch ? `page=search` : '',
      })
    }

    const onClickRangeDate = () => {
      navigate({
        pathname: RoutingPaths.avia.calendar.path(),
        search: isSearch ? `page=search` : '',
      })
    }

    const onSwitchDirection = () => {
      onSwitch?.()
    }

    const onSubmitCountAndClass = (data?: CountAndClass) => {
      onChange?.(data)

      closeDialog()
    }

    const onClickDetail = () => {
      const direction = toJS(searchData?.directions?.[0]) || {}

      openDialog({
        child: (
          <LevelClassNCount
            directions={toJS([direction])}
            data={{
              adult: searchData.adult,
              infant: searchData.infant,
              infants_with_place: searchData.infants_with_place,
              child: searchData.child,
              cabin_class: String(searchData.cabin_class),
            }}
            onChangeCountHandler={onSubmitCountAndClass}
          />
        ),
        maxHeight: '95vh',
      })
    }

    const resultDate = dateFormat(directionData?.date || '')
    const resultReturnDate = dateFormat(directionData?.back_date || '')

    const totalCountPassengers =
      (searchData?.adult || 0) +
      (searchData?.child || 0) +
      (searchData?.infant || 0) +
      (searchData?.infants_with_place || 0)

    const currentTypeCabin = useMemo(
      () =>
        TicketClassList?.find((item) => item?.key === searchData?.cabin_class),
      [searchData?.cabin_class],
    )

    const validateForm = async () => {
      try {
        const formToValidate = {
          ...searchData,
          directions: normalizeRegularSearchDirections(searchData.directions),
        }

        await checkFormFindTicket.validate(formToValidate, {
          abortEarly: false,
        })
        setErrors(undefined)

        findTicketFormStore.onPrepareRegularSearch(isSearch)
        onSearch?.()
      } catch (error) {
        const resultValidate = getFieldsError(error)

        setErrors(resultValidate)
      }
    }

    return (
      <div>
        <div className={s.block}>
          <DepArrCountryBlock
            type={type}
            from={{
              name: directionData?.from?.cityName,
              code: directionData?.from?.cityIataCode,
            }}
            to={{
              name: directionData?.to?.cityName,
              code: directionData?.to?.cityIataCode,
            }}
            onClick={onClickToSearch}
            onSwitch={onSwitchDirection}
            error={
              !!errors?.['directions[0].from.cityIataCode'] ||
              !!errors?.['directions[0].to.cityIataCode']
            }
          />
        </div>

        <div className={clsx(s.dateWrapper, s.block)}>
          <div className={s.date}>
            <ButtonIconText
              radius={16}
              height="100%"
              type={type}
              value={resultDate}
              placeholder={t('when')}
              onClick={onClickRangeDate}
              error={!!errors?.['directions[0].date']}
            />
          </div>

          <div className={s.date}>
            <ButtonIconText
              height="100%"
              radius={16}
              type={type}
              value={resultReturnDate}
              placeholder={t('return')}
              onClick={onClickRangeDate}
            />
          </div>
        </div>

        <div className={s.block}>
          <div className={s.passenger}>
            <ButtonIconText
              type={type}
              radius={16}
              height="100%"
              onClick={onClickDetail}
              placeholder={t('passengers_and_class')}
              rightIcon={AllIcons.PASSENGERS}
              value={`${totalCountPassengers}, ${t(currentTypeCabin?.name || '')}`}
            />
          </div>
        </div>

        <Button
          className={s.button}
          onClick={validateForm}
          borderRadius={'md'}
        >
          <p className={s.text}>{t('find_tickets')}</p>
        </Button>
      </div>
    )
  },
)
